import {
  camelCase, keys, includes,
  has, get, pickBy,
} from 'lodash';
import units from '../../lib/units';
import numericInput from '../../lib/numericInput';
import {
  inputKeys,
  conversions,
  isElectricVehicle,
  isVehicleEfficiency,
  getPrecision,
  isResult,
} from '../../lib/apiKeys';

const winterFuelTypes = {
  NATURAL_GAS: 0,
  ELECTRICITY: 1,
  HEATING_OIL: 2,
};

export const ontarioMetricDefaults = {
  input_takeaction_assumption_price_of_gasoline: 1.2,
  input_takeaction_assumption_ngprice: 0.15,
  input_footprint_housing_cleanpercent: 1,
  input_takeaction_purchase_green_electricity_percentclean: 1,
};

/* eslint-disable no-use-before-define */
const convertWinterFuelCarbonAreaIntensity = fuelTypeApiKey =>
  (apiKey, value, unitSystem, state) => {
    if (units.isStandard(unitSystem)) return value;
    const fuelType =
      getEnsuredNumericInput(state, fuelTypeApiKey, unitSystem);
    const convertedForArea = value * Math.pow(units.FT_PER_METER, 2);
    switch (fuelType) {
      case winterFuelTypes.NATURAL_GAS: return units.cuFtToCuM(convertedForArea);
      case winterFuelTypes.ELECTRICITY: return convertedForArea;
      case winterFuelTypes.HEATING_OIL: return units.galToLt(convertedForArea);
      default: {
        throw new Error(`Unexpected winter fuel type ${fuelType}.`);
      }
    }
  };

const convertWinterFuelCarbonIntensity = fuelTypeApiKey =>
  (apiKey, value, unitSystem, state) => {
    if (units.isStandard(unitSystem)) return value;
    const fuelType =
    getEnsuredNumericInput(state, fuelTypeApiKey, unitSystem);
    switch (fuelType) {
      case winterFuelTypes.NATURAL_GAS: return value * Math.pow(units.FT_PER_METER, 3);
      case winterFuelTypes.ELECTRICITY: return value;
      case winterFuelTypes.HEATING_OIL: return value / units.LT_PER_GAL;
      default: {
        throw new Error(`Unexpected winter fuel type ${fuelType}.`);
      }
    }
  };

const convertWinterFuelVolume = fuelTypeApiKey =>
  (apiKey, value, unitSystem, state) => {
    if (units.isStandard(unitSystem)) return value;
    const fuelType =
    getEnsuredNumericInput(state, fuelTypeApiKey, unitSystem);
    switch (fuelType) {
      case winterFuelTypes.NATURAL_GAS: return units.cuFtToCuM(value);
      case winterFuelTypes.ELECTRICITY: return value;
      case winterFuelTypes.HEATING_OIL: return units.galToLt(value);
      default: {
        throw new Error(`Unexpected winter fuel type ${fuelType}.`);
      }
    }
  };
/* eslint-enable no-use-before-define */

const customConversions = {
  result_takeaction_thermostat_winter_intensity:
    convertWinterFuelCarbonAreaIntensity('input_takeaction_thermostat_winter_fueltype'),
  result_takeaction_purchase_high_efficiency_heating_intensity:
    convertWinterFuelCarbonAreaIntensity('input_takeaction_purchase_high_efficiency_heating_fueltype'),
  result_takeaction_thermostat_winter_gCO2fueltype:
    convertWinterFuelCarbonIntensity('input_takeaction_thermostat_winter_fueltype'),
  result_takeaction_purchase_high_efficiency_heating_gCO2fueltype:
    convertWinterFuelCarbonIntensity('input_takeaction_purchase_high_efficiency_heating_fueltype'),
  result_takeaction_thermostat_winter_pu: convertWinterFuelVolume('input_takeaction_thermostat_winter_fueltype'),
  result_takeaction_purchase_high_efficiency_heating_pu:
    convertWinterFuelVolume('input_takeaction_purchase_high_efficiency_heating_fueltype'),
};

/**
 * This function assumes that the value passed in is
 * opposite of the requested unitSystem.
 * @param {String} apiKey
 * @param {Number} value
 * @param {String} unitSystem unit system to convert to.
 * @param {Object} state Redux state.
 * @param {?String} forceFromUnitSystem
 */
export const convertValue = (apiKey, value, unitSystem, state, forceFromUnitSystem) => {
  let fromUnitSystem = forceFromUnitSystem;
  if (!fromUnitSystem) {
    fromUnitSystem = isResult(apiKey) ? units.STANDARD : state.ui.unitSystem;
  }
  if (fromUnitSystem === unitSystem) return value;

  const isMetric = units.isMetric(unitSystem);
  const isFromMetric = units.isMetric(fromUnitSystem);
  if (has(customConversions, apiKey)) {
    return customConversions[apiKey](apiKey, value, unitSystem, state);
  } else if (isVehicleEfficiency(apiKey)) {
    const isElectric = isElectricVehicle(apiKey, state);
    if (isMetric) {
      return units.convertFuelEfficiencyToMetric(value, isFromMetric, isElectric);
    }
    return units.convertFuelEfficiencyToStandard(value, isFromMetric, isElectric);
  } else if (includes(conversions.galLt, apiKey)) {
    if (isMetric && !includes(conversions.inverse.galLt, apiKey)) {
      return units.galToLt(value);
    } else if (!isMetric && includes(conversions.inverse.galLt, apiKey)) {
      return units.galToLt(value);
    }
    return units.ltToGal(value);
  } else if (includes(conversions.milesKm, apiKey)) {
    if (isMetric && !includes(conversions.inverse.milesKm, apiKey)) {
      return units.miToKm(value);
    } else if (!isMetric && includes(conversions.inverse.milesKm, apiKey)) {
      return units.miToKm(value);
    }
    return units.kmToMi(value);
  } else if (includes(conversions.cuftCum, apiKey)) {
    if (isMetric && !includes(conversions.inverse.cuftCum, apiKey)) {
      return units.cuFtToCuM(value);
    } else if (!isMetric && includes(conversions.inverse.cuftCum, apiKey)) {
      return units.cuFtToCuM(value);
    }
    return units.cuMToCuFt(value);
  } else if (includes(conversions.sqFtSqM, apiKey)) {
    if (isMetric && !includes(conversions.inverse.sqFtSqM, apiKey)) {
      return units.sqFtToSqM(value);
    } else if (!isMetric && includes(conversions.inverse.sqFtSqM, apiKey)) {
      return units.sqFtToSqM(value);
    }
    return units.sqMToSqFt(value);
  } else if (includes(conversions.ngThermsCum, apiKey)) {
    if (isMetric && !includes(conversions.inverse.ngThermsCum, apiKey)) {
      return units.naturalGasThermsToCubicMeters(value);
    } else if (!isMetric && includes(conversions.inverse.ngThermsCum, apiKey)) {
      return units.naturalGasThermsToCubicMeters(value);
    }
    return units.naturalGasCubicMetersToTherms(value);
  } else if (includes(conversions.mpgLtPer100Km, apiKey)) {
    if (isMetric) return units.mpgToLtPer100Km(value);
    return units.ltPer100KmToMpg(value);
  } else if (includes(conversions.farenheitCelsiusDiff, apiKey)) {
    if (isMetric) return units.farenheitToCelsiusDiff(value);
    return units.celsiusToFarenheitDiff(value);
  } else if (includes(conversions.farenheitCelsius, apiKey)) {
    if (isMetric) return units.farenheitToCelsius(value);
    return units.celsiusToFarenheit(value);
  } else if (includes(conversions.cuyCum, apiKey)) {
    if (isMetric) return units.cuyToCum(value);
    return units.cumToCuy(value);
  }
  return value;
};

/**
 * @param {Object} state Redux state.
 * @param {String} key API key.
 * @returns {String|undefined} Input from user. If value is number, it is
 * parsed, rounded, and cast to locale string..
 */
export const getUiInput = (state, key) => {
  const inputs = state.user_footprint.inputs;
  if (!has(inputs, key) || !numericInput.isParseable(inputs[key])) return '';

  const value = inputs[key];
  if (typeof value === 'string') return value;
  return numericInput.format(numericInput.parse(value), { decimalPlaces: getPrecision(key) });
};

/**
 * @param {Object} state Redux state.
 * @param {String} key API key.
 * @param {String=} unitSystem Defaults to current unitSystem from state.ui.
 * @param {Object=} opts Defaults to current unitSystem from state.ui.
 * @param {Boolean} [opts.pullMonthlyValues=false] Set to true if monthly input values
 * should be anualized.
 * @returns {Number|undefined} Parsed input value converted to requested unitSystem.
 */
export const getNumericInput = (state, key, unitSystem, { pullMonthlyValues = true } = {}) => {
  const inputs = state.user_footprint.inputs;
  if (!has(inputs, key) || !numericInput.isParseable(inputs[key])) return undefined;

  const uiUnitSystem = state.ui.unitSystem;
  let value = numericInput.parse(inputs[key]);
  if (!pullMonthlyValues && get(state.ui, ['monthlyInputs', key], false)) {
    value *= 12.0;
  }
  if (unitSystem === undefined || unitSystem === uiUnitSystem) return value;
  return convertValue(key, value, unitSystem, state);
};

const noDefaultApiKeys = [
  'input_footprint_transportation_airshort',
  'input_footprint_transportation_airmedium',
  'input_footprint_transportation_airlong',
  'input_footprint_transportation_airextended',
];

/**
 * @param {Object} state Redux state.
 * @param {String} key API key.
 * @param {String=} unitSystem Defaults to current unitSystem from state.ui.
 * @param {Object=} opts
 * @param {Boolean} [opts.pullMonthlyValues=true] Set to true if monthly input values
 * should be anualized.
 * @returns {Number} Default api value converted to requested unitSystem.
 */
export const getNumericDefault = (state, key, unitSystem, { pullMonthlyValues = true } = {}) => {
  const defaults = state.average_footprint.get_defaults_and_results;
  let value = defaults[key];
  if (!numericInput.isParseableEn(value)) {
    if (noDefaultApiKeys.includes(key)) {
      return 0;
    }
    throw new Error(`No default value for ${key}`);
  }

  value = numericInput.parseEn(value);
  const uiUnitSystem = state.ui.unitSystem;
  if (pullMonthlyValues && get(state.ui, ['monthlyInputs', key], false)) {
    value /= 12;
  }
  if (!units.isMetric(unitSystem || uiUnitSystem)) return value;
  return convertValue(key, value, units.METRIC, state, units.STANDARD);
};

export const getEnsuredNumericInput = (state, key, unitSystem, {
  pullMonthlyValues = true,
} = {}) => {
  const inputValue = getNumericInput(state, key, unitSystem, { pullMonthlyValues });
  if (inputValue !== undefined) return inputValue;
  return getNumericDefault(state, key, unitSystem, { pullMonthlyValues });
};

/**
 * Returns all api values set by user in requested unitSystem.
 * @param {Object} state Redux state.
 * @param {String} unitSystem
 * @return {Object.<String, String|Number>}
 */
export const inputValues = (state, unitSystem) => {
  const values = keys(state.user_footprint.inputs)
    .reduce((converted, apiKey) => ({
      ...converted,
      [apiKey]: getNumericInput(state, apiKey, unitSystem),
    }), {});
  return pickBy(values, value => value !== undefined);
};

/**
 * Returns parsed values for all API keys. Values fallback to
 * average footprint defaults if user has net set input.
 * @param {Object} state Redux state.
 * @param {String} unitSystem
 * @return {Object.<String, Number>}
 */
export const ensuredInputValues = (state, unitSystem, { pullMonthlyValues = true } = {}) => {
  const values = keys(state.average_footprint.get_defaults_and_results)
    .filter(apiKey => /^input_(footprint|takeaction)/.test(apiKey))
    .reduce((previous, apiKey) => {
      let value = getNumericInput(state, apiKey, unitSystem, { pullMonthlyValues });
      if (value === undefined) {
        value = getNumericDefault(state, apiKey, unitSystem, { pullMonthlyValues });
      }
      return {
        ...previous,
        [apiKey]: value,
      };
    }, {});
  if (values.input_footprint_transportation_airtype === 1) {
    values.input_footprint_transportation_airshort = getEnsuredNumericInput(state, 'input_footprint_transportation_airshort', unitSystem, { pullMonthlyValues });
    values.input_footprint_transportation_airmedium = getEnsuredNumericInput(state, 'input_footprint_transportation_airmedium', unitSystem, { pullMonthlyValues });
    values.input_footprint_transportation_airlong = getEnsuredNumericInput(state, 'input_footprint_transportation_airlong', unitSystem, { pullMonthlyValues });
    values.input_footprint_transportation_airextended = getEnsuredNumericInput(state, 'input_footprint_transportation_airextended', unitSystem, { pullMonthlyValues });
  }
  return values;
};

const electricCarDefaultEfficiency = (isMetric) => {
  const efficiency = 30;
  if (isMetric) return efficiency / units.KM_PER_MI; // kWh/100-km
  return efficiency; // kWh/100-mi
};

/**
 * An object of selectors for each api input key.
 * @typedef {Object.<String, Function>} InputSelectors
 */
export default inputKeys.reduce((selectors, key) => ({
  ...selectors,
  [camelCase(key)]: (state) => {
    let defaultValue = getNumericDefault(state, key);
    if (isVehicleEfficiency(key)) {
      defaultValue = electricCarDefaultEfficiency(units.isMetric(state.ui.unitSystem));
    }
    const defaultDisplay = numericInput.format(defaultValue, { decimalPlaces: getPrecision(key) });
    const input = getNumericInput(state, key);
    const inputDisplay = getUiInput(state, key);
    return {
      inputDisplay,
      input,
      defaultDisplay,
      default: defaultValue,
      ensured: input || defaultValue,
      ensuredDisplay: inputDisplay || defaultDisplay,
    };
  },
}), {});
